import React, { useState, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import './LocationDetailModal.css';

const LocationDetailModal = ({ location, isOpen, onClose, onRoute }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [rating, setRating] = useState(5);
  const [userName, setUserName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // 📱 QR Code States
  const [qrDownloading, setQrDownloading] = useState(false);
  
  // 🖼️ Photo Gallery States
  const [activePhotoTab, setActivePhotoTab] = useState('front');
  
  // ✅ Business Verification States
  const [isVerified, setIsVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending');

  const photoTabs = [
    { id: 'front', label: '🏠 Front Photo', icon: '🏠' },
    { id: 'inside', label: '🪑 Inside Photo', icon: '🪑' },
    { id: 'parking', label: '🚗 Parking', icon: '🚗' }
  ];

  // Load comments and check verification
  useEffect(() => {
    if (location && isOpen) {
      // Load comments from localStorage
      const savedComments = localStorage.getItem(`comments_${location.id}`);
      if (savedComments) {
        setComments(JSON.parse(savedComments));
      } else {
        // Sample comments
        setComments([
          {
            id: 1,
            user: 'မောင်မောင်',
            rating: 4,
            comment: 'အားလုံးကို နှုတ်ခွန်းဆက်သပါတယ်ခင်ဗျာ။',
            date: '၂ ရက်အကြာ'
          },
          {
            id: 2,
            user: 'မမ',
            rating: 3,
            comment: 'ပျော်ရွှင်စရာနေ့ရက်တွေကို အစဉ်ထာဝရ ဖြတ်သန်းနိုင်ကြပါစေ။',
            date: '၅ ရက်အကြာ'
          }
        ]);
      }

      // Check if user is logged in
      const user = localStorage.getItem('username');
      if (user) {
        setIsLoggedIn(true);
        setUserName(user);
      }

      // Check verification status from localStorage
      const savedVerification = localStorage.getItem(`verified_${location.id}`);
      if (savedVerification === 'true') {
        setIsVerified(true);
        setVerificationStatus('verified');
      } else {
        setIsVerified(false);
        setVerificationStatus('pending');
      }
    }
  }, [location, isOpen]);

  // 📥 Download QR Code
  const handleDownloadQR = () => {
    setQrDownloading(true);
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `qr-${location.id || 'location'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
    setQrDownloading(false);
  };

  // 📤 Share QR Code
  const handleShareQR = async () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      try {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        const file = new File([blob], `qr-${location.id || 'location'}.png`, { type: 'image/png' });
        
        if (navigator.share) {
          await navigator.share({
            title: location.name_mm || 'Location',
            text: `📍 ${location.name_mm || ''} - ${location.address || ''}`,
            files: [file]
          });
        } else {
          // Fallback: Copy link to clipboard
          const url = `${window.location.origin}/location/${location.id || ''}`;
          await navigator.clipboard.writeText(url);
          alert('✅ Link ကို clipboard ထဲသို့ ကူးထည့်ပြီးပါပြီ။');
        }
      } catch (error) {
        console.error('Share error:', error);
        // Fallback: Copy link
        const url = `${window.location.origin}/location/${location.id || ''}`;
        await navigator.clipboard.writeText(url);
        alert('✅ Link ကို clipboard ထဲသို့ ကူးထည့်ပြီးပါပြီ။');
      }
    }
  };

  // 📤 Send OTP
  const handleSendOTP = async () => {
    if (!phoneNumber) {
      alert('ကျေးဇူးပြု၍ ဖုန်းနံပါတ်ထည့်ပါ');
      return;
    }

    setIsSendingOTP(true);
    try {
      const mockOTP = Math.floor(100000 + Math.random() * 900000);
      console.log('📱 Mock OTP for testing:', mockOTP);
      
      localStorage.setItem(`otp_${phoneNumber}`, mockOTP);
      localStorage.setItem(`otp_time_${phoneNumber}`, Date.now());
      
      setOtpSent(true);
      alert(`✅ OTP Code ပို့ပြီးပါပြီ။ (Testing: ${mockOTP})`);
    } catch (error) {
      alert('❌ OTP ပို့ရာတွင် အမှားရှိပါသည်။');
    } finally {
      setIsSendingOTP(false);
    }
  };

  // ✅ Verify OTP
  const handleVerifyOTP = async () => {
    if (!otpCode) {
      alert('ကျေးဇူးပြု၍ OTP Code ထည့်ပါ');
      return;
    }

    try {
      const savedOTP = localStorage.getItem(`otp_${phoneNumber}`);
      const savedTime = localStorage.getItem(`otp_time_${phoneNumber}`);
      
      const isExpired = savedTime && (Date.now() - parseInt(savedTime) > 5 * 60 * 1000);
      
      if (savedOTP && savedOTP === otpCode && !isExpired) {
        setIsVerified(true);
        setVerificationStatus('verified');
        localStorage.setItem(`verified_${location.id}`, 'true');
        setOtpSent(false);
        setOtpCode('');
        setPhoneNumber('');
        alert('✅ အောင်မြင်ပါသည်။ သင့်ဆိုင်ကို Verify လုပ်ပြီးပါပြီ။');
      } else if (isExpired) {
        alert('❌ OTP သက်တမ်းကုန်သွားပါပြီ။ ပြန်ပို့ပါ။');
        setOtpSent(false);
      } else {
        alert('❌ OTP Code မှားယွင်းနေပါသည်။ ထပ်စမ်းပါ။');
      }
    } catch (error) {
      alert('❌ Verification လုပ်ရာတွင် အမှားရှိပါသည်။');
    }
  };

  // Save comments
  const saveComments = (newComments) => {
    localStorage.setItem(`comments_${location.id}`, JSON.stringify(newComments));
  };

  // Handle comment submit
  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const commentData = {
      id: Date.now(),
      user: userName || 'ဧည့်သည်',
      rating: rating,
      comment: newComment,
      date: 'ယခုလေးတင်'
    };

    const updatedComments = [commentData, ...comments];
    setComments(updatedComments);
    saveComments(updatedComments);
    setNewComment('');
    setRating(5);
  };

  // Handle route
  const handleRouteClick = () => {
    if (onRoute && location) {
      onRoute(location.lat, location.lng, location.name_mm);
    }
    onClose();
  };

  // Format rating stars
  const renderStars = (rating) => {
    return '⭐'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  // Debug - Check if modal should render
  console.log('📍 LocationDetailModal - isOpen:', isOpen, 'location:', location?.name_mm);

  if (!isOpen || !location) {
    console.log('❌ Modal not open or location is null');
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose}>✕</button>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-business-icon">📍</div>
          <div>
            <h2>{location.name_mm || 'အမည်မရှိ'}</h2>
            <p className="modal-business-sub">{location.name_en || ''}</p>
            {verificationStatus === 'verified' && (
              <span className="verified-badge">✅ Verified</span>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="modal-body">
          <div className="modal-row">
            {/* Left Column - Image */}
            <div className="modal-left">
              {location.photo_url ? (
                <img 
                  src={location.photo_url} 
                  alt={location.name_mm} 
                  className="modal-image"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                  }}
                />
              ) : (
                <div className="modal-no-image">📷 ဓာတ်ပုံမရှိပါ</div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="modal-right">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-icon">🏷️</span>
                  <span className="detail-label">အမျိုးအစား:</span>
                  <span className="detail-value">{location.category || 'မရှိ'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📂</span>
                  <span className="detail-label">လုပ်ငန်းခွဲ:</span>
                  <span className="detail-value">{location.subcategory || 'မရှိ'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📍</span>
                  <span className="detail-label">မြို့နယ်:</span>
                  <span className="detail-value">{location.township || 'မရှိ'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">📞</span>
                  <span className="detail-label">ဖုန်း:</span>
                  <span className="detail-value">{location.phone || 'မရှိ'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">⭐</span>
                  <span className="detail-label">အဆင့်သတ်မှတ်:</span>
                  <span className="detail-value">{location.rating || 'N/A'}/5</span>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">🕐</span>
                  <span className="detail-label">အချိန်:</span>
                  <span className="detail-value">{location.opening_hours || 'မရှိ'}</span>
                </div>
                <div className="detail-item full-width">
                  <span className="detail-icon">📌</span>
                  <span className="detail-label">လိပ်စာ:</span>
                  <span className="detail-value">{location.address || 'မရှိ'}</span>
                </div>

                {/* Social Media Links */}
                <div className="detail-item full-width social-links">
                  <span className="detail-icon">🌐</span>
                  <span className="detail-label">Social Media:</span>
                  <div className="social-icons">
                    {location.facebook && (
                      <a href={location.facebook} target="_blank" rel="noopener noreferrer" className="social-link facebook">
                        📘
                      </a>
                    )}
                    {location.tiktok && (
                      <a href={location.tiktok} target="_blank" rel="noopener noreferrer" className="social-link tiktok">
                        🎵
                      </a>
                    )}
                    {location.youtube && (
                      <a href={location.youtube} target="_blank" rel="noopener noreferrer" className="social-link youtube">
                        ▶️
                      </a>
                    )}
                    {location.messenger && (
                      <a href={location.messenger} target="_blank" rel="noopener noreferrer" className="social-link messenger">
                        💬
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 🖼️ Photo Gallery Section */}
          <div className="photo-gallery-section">
            <h4>🖼️ Photo Gallery</h4>
            <div className="photo-tabs">
              {photoTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`photo-tab ${activePhotoTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActivePhotoTab(tab.id)}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
            <div className="photo-display">
              {location.photos && location.photos[activePhotoTab] ? (
                <img 
                  src={location.photos[activePhotoTab]} 
                  alt={`${activePhotoTab} photo`}
                  className="gallery-photo"
                />
              ) : (
                <div className="no-photo">
                  📷 {activePhotoTab === 'front' ? 'မျက်နှာပုံ' : 
                      activePhotoTab === 'inside' ? 'အတွင်းပုံ' : 'ကားပါကင်'} မရှိသေးပါ
                </div>
              )}
            </div>
          </div>

          {/* 📱 QR Code Share Section */}
          <div className="qr-code-section">
            <h4>📱 QR Code Share</h4>
            <div className="qr-code-container">
              <QRCodeCanvas 
                id="qr-code-canvas"
                value={`${window.location.origin}/location/${location.id || ''}`}
                size={120}
                level="H"
                includeMargin={true}
              />
              <div className="qr-actions">
                <button onClick={handleDownloadQR} disabled={qrDownloading}>
                  {qrDownloading ? '⏳' : '📥 Download QR'}
                </button>
                <button onClick={handleShareQR}>📤 Share QR</button>
              </div>
              <p className="qr-hint">🔍 Scan လုပ်ပြီး Detail Page ကိုဖွင့်ပါ</p>
            </div>
          </div>

          {/* ✅ Business Verification Section */}
          <div className="verification-section">
            <h4>✅ Business Verification</h4>
            {verificationStatus === 'verified' ? (
              <div className="verified-badge-large">
                <span className="verified-icon">✅</span>
                <div>
                  <strong>Verified Business</strong>
                  <p>ဤဆိုင်သည် စစ်ဆေးအတည်ပြုပြီးဖြစ်သည်</p>
                </div>
              </div>
            ) : (
              <div className="unverified-section">
                <p>🔐 ဆိုင်ရှင်ဖြစ်ကြောင်း အတည်ပြုရန်</p>
                {!otpSent ? (
                  <div className="otp-send-form">
                    <input
                      type="tel"
                      placeholder="📞 ဖုန်းနံပါတ်ထည့်ပါ"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="otp-input"
                    />
                    <button 
                      onClick={handleSendOTP} 
                      className="otp-send-btn"
                      disabled={isSendingOTP}
                    >
                      {isSendingOTP ? '⏳ ပို့နေသည်...' : '📤 OTP ပို့ရန်'}
                    </button>
                  </div>
                ) : (
                  <div className="otp-verify-form">
                    <input
                      type="text"
                      placeholder="🔑 OTP Code ထည့်ပါ"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      className="otp-input"
                      maxLength="6"
                    />
                    <div className="otp-actions">
                      <button onClick={handleVerifyOTP} className="otp-verify-btn">
                        ✅ Verify လုပ်မည်
                      </button>
                      <button onClick={() => setOtpSent(false)} className="otp-resend-btn">
                        🔄 ပြန်ပို့ရန်
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="comments-section">
            <h4>📝 မှတ်ချက်များ ({comments.length})</h4>
            
            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">မှတ်ချက်မရှိသေးပါ။ ပထမဆုံးမှတ်ချက်ပေးပါ။</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <span className="comment-user">👤 {comment.user}</span>
                      <span className="comment-rating">{renderStars(comment.rating)}</span>
                      <span className="comment-date">{comment.date}</span>
                    </div>
                    <p className="comment-text">{comment.comment}</p>
                  </div>
                ))
              )}
            </div>

            <div className="comment-form">
              <h4>💬 မှတ်ချက်ရေးရန်</h4>
              <form onSubmit={handleSubmitComment}>
                {!isLoggedIn && (
                  <input
                    type="text"
                    placeholder="သင့်အမည်"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="comment-name-input"
                    required
                  />
                )}
                <textarea
                  placeholder="သင့်မှတ်ချက်ကိုရေးပါ..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="comment-textarea"
                  rows="3"
                  required
                />
                <div className="comment-rating-input">
                  <span>အဆင့်သတ်မှတ်:</span>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star-btn ${star <= rating ? 'active' : ''}`}
                      onClick={() => setRating(star)}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
                <button type="submit" className="comment-submit-btn">
                  📤 မှတ်ချက်ပေးမည်
                </button>
              </form>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            <button className="action-btn route-btn" onClick={handleRouteClick}>
              🗺️ လမ်းကြောင်းပြရန်
            </button>
            <button className="action-btn call-btn" onClick={() => window.location.href = `tel:${location.phone}`}>
              📞 ဆက်သွယ်ရန်
            </button>
            <button className="action-btn share-btn" onClick={() => navigator.clipboard.writeText(window.location.href)}>
              📤 မျှဝေရန်
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationDetailModal;